/**
 * notificaciones.js
 * Servicio de notificaciones persistentes en localStorage.
 * Solo visible para el superadministrador.
 * Usa CustomEvents para comunicación entre componentes sin tocar el código existente.
 */

const STORAGE_KEY = 'admin_notificaciones';
const MAX_NOTIFICACIONES = 50; // Máximo de notificaciones a guardar
const DISMISSED_PAGOS_KEY = 'admin_pagos_notifs_descartadas';

// Íconos por módulo (emoji)
const ICONOS_MODULO = {
  // Ventas
  venta: '🛒',
  pago: '💳',
  // Clientes
  cliente: '👤',
  // Productos
  producto: '📦',
  // Recepciones
  recepcion: '🖥️',
  recepcionExpress: '⚡',
  // Estados
  estado: '🔄',
  // Usuarios
  usuario: '👥',
  // Roles
  rol: '🎭',
  // Permisos
  permiso: '🔐',
  // Proveedores
  proveedor: '🏭',
  // Compras
  compra: '🛍️',
  // Créditos
  credito: '💰',
  // Servicios
  servicio: '🔧',
  // Categorías
  categoria: '🗂️',
  // Tasas de cambio
  tasa: '💱',
  // Caja
  caja: '🏦',
  // Técnicos
  tecnico: '👨‍🔧',
  // Herramientas
  herramienta: '🔨',
  // Sedes
  sede: '🏢',
  // Garantías
  garantia: '✅',
  // Default
  default: '🔔',
};

/**
 * Obtiene el ícono para un módulo dado.
 */
function getIcono(modulo) {
  if (!modulo) return ICONOS_MODULO.default;
  const key = Object.keys(ICONOS_MODULO).find(k =>
    modulo.toLowerCase().includes(k.toLowerCase())
  );
  return key ? ICONOS_MODULO[key] : ICONOS_MODULO.default;
}

/**
 * Carga las notificaciones desde localStorage.
 */
export function cargarNotificaciones() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Guarda las notificaciones en localStorage.
 */
function guardarNotificaciones(lista) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lista));
  } catch {
    // ignorar si localStorage está lleno
  }
}

/**
 * Obtiene la lista de IDs de pagos descartados/borrados por el usuario.
 */
export function obtenerPagosDescartados() {
  try {
    const raw = localStorage.getItem(DISMISSED_PAGOS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Registra un idPago como descartado para que no se vuelva a reportar ni crear notificación.
 */
export function descartarNotificacionPago(idPago) {
  if (!idPago) return;
  try {
    const descartados = obtenerPagosDescartados();
    const idStr = String(idPago);
    if (!descartados.some(id => String(id) === idStr)) {
      descartados.push(idPago);
      localStorage.setItem(DISMISSED_PAGOS_KEY, JSON.stringify(descartados));
    }
  } catch (e) {
    console.error('Error descartando notificación de pago:', e);
  }
}

/**
 * Remueve un idPago de la lista de descartados (ej: si se completó o cambió de estado).
 */
export function olvidarPagoDescartado(idPago) {
  if (!idPago) return;
  try {
    const descartados = obtenerPagosDescartados();
    const idStr = String(idPago);
    const filtrados = descartados.filter(id => String(id) !== idStr);
    localStorage.setItem(DISMISSED_PAGOS_KEY, JSON.stringify(filtrados));
  } catch (e) {
    console.error('Error olvidando pago descartado:', e);
  }
}

/**
 * Agrega una nueva notificación.
 * @param {Object} opciones
 * @param {string} opciones.modulo  - Módulo que originó la notificación (ej: 'venta', 'cliente')
 * @param {string} opciones.accion  - Acción realizada (ej: 'Creado', 'Editado', 'Eliminado')
 * @param {string} opciones.titulo  - Título descriptivo
 * @param {string} [opciones.detalle] - Detalle adicional opcional
 */
export function agregarNotificacion({ modulo, accion, titulo, detalle = '', idPago = null, ruta = null }) {
  const isSuperAdmin = verificarSuperAdmin();
  if (!isSuperAdmin) return;

  // Si este pago fue borrado/descartado previamente por el usuario, no volver a crearlo
  if (idPago) {
    const descartados = obtenerPagosDescartados();
    if (descartados.some(id => String(id) === String(idPago))) {
      return;
    }
  }

  let lista = cargarNotificaciones();
  // Evitar duplicar notificación para el mismo idPago si ya está registrada
  if (idPago && lista.some(n => n.idPago === idPago || n.id === `pago-${idPago}`)) {
    return;
  }

  const icono = getIcono(modulo);
  const ahora = new Date();
  const nuevaNotif = {
    id: idPago ? `pago-${idPago}` : (Date.now() + Math.random()),
    idPago: idPago || null,
    ruta: ruta || (modulo === 'pago' ? '/pagos' : null),
    modulo,
    accion,
    titulo,
    detalle,
    icono,
    leida: false,
    fecha: ahora.toISOString(),
    fechaFormateada: formatearFecha(ahora),
    horaFormateada: formatearHora(ahora),
  };

  lista.unshift(nuevaNotif);

  if (lista.length > MAX_NOTIFICACIONES) {
    lista = lista.slice(0, MAX_NOTIFICACIONES);
  }

  guardarNotificaciones(lista);
  window.dispatchEvent(new CustomEvent('notificacionNueva', { detail: nuevaNotif }));
}

/**
 * Marca una notificación de pago como verificada/leída cuando se completa en /pagos
 */
export function resolverNotificacionPago(idPago) {
  let lista = cargarNotificaciones();
  let modificado = false;
  lista = lista.map(n => {
    if (n.idPago === idPago || n.id === `pago-${idPago}`) {
      modificado = true;
      return { ...n, leida: true, accion: 'Verificado', titulo: 'Pago online de servicios verificado' };
    }
    return n;
  });
  if (modificado) {
    guardarNotificaciones(lista);
    window.dispatchEvent(new CustomEvent('notificacionesActualizadas'));
  }
}

/**
 * Marca todas las notificaciones como leídas.
 */
export function marcarTodasLeidas() {
  const lista = cargarNotificaciones().map(n => ({ ...n, leida: true }));
  guardarNotificaciones(lista);
  window.dispatchEvent(new CustomEvent('notificacionesActualizadas'));
}

/**
 * Marca una notificación específica como leída.
 */
export function marcarLeida(id) {
  const lista = cargarNotificaciones().map(n =>
    n.id === id ? { ...n, leida: true } : n
  );
  guardarNotificaciones(lista);
  window.dispatchEvent(new CustomEvent('notificacionesActualizadas'));
}

/**
 * Elimina una sola notificación por ID.
 * Si corresponde a un pago, lo registra como descartado para evitar que reaparezca.
 */
export function eliminarNotificacion(id) {
  let lista = cargarNotificaciones();
  const notif = lista.find(n => n.id === id);
  if (notif && notif.idPago) {
    descartarNotificacionPago(notif.idPago);
  }
  lista = lista.filter(n => n.id !== id);
  guardarNotificaciones(lista);
  window.dispatchEvent(new CustomEvent('notificacionesActualizadas'));
}

/**
 * Elimina todas las notificaciones.
 * Si existen notificaciones de pagos, las descarta para que no se recreen inmediatamente.
 */
export function limpiarNotificaciones() {
  const lista = cargarNotificaciones();
  for (const n of lista) {
    if (n.idPago) {
      descartarNotificacionPago(n.idPago);
    }
  }
  guardarNotificaciones([]);
  window.dispatchEvent(new CustomEvent('notificacionesActualizadas'));
}

/**
 * Obtiene el conteo de notificaciones no leídas.
 */
export function contarNoLeidas() {
  return cargarNotificaciones().filter(n => !n.leida).length;
}

/**
 * Verifica si el usuario logueado es superadministrador.
 */
export function verificarSuperAdmin() {
  try {
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    const rolLocal = (localStorage.getItem('rol') || '').toLowerCase();
    if (!userStr && !rolLocal) return true;
    let rol = rolLocal;
    if (userStr) {
      const user = JSON.parse(userStr);
      rol = (user.nombreRol || user.rol?.nombre || rolLocal).toLowerCase();
    }
    return rol.includes('superadmin') || rol.includes('super admin') || rol.includes('administrador') || rol === 'admin' || rol.includes('recep') || rol.includes('caj') || rol.includes('geren');
  } catch {
    return true;
  }
}

// ==================== Helpers de formato ====================

function formatearFecha(fecha) {
  return fecha.toLocaleDateString('es-VE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatearHora(fecha) {
  return fecha.toLocaleTimeString('es-VE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Devuelve tiempo relativo (ej: "hace 5 min").
 */
export function tiempoRelativo(fechaIso) {
  try {
    const diff = Date.now() - new Date(fechaIso).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'ahora mismo';
    if (min < 60) return `hace ${min} min`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `hace ${hrs} h`;
    const dias = Math.floor(hrs / 24);
    return `hace ${dias} día${dias > 1 ? 's' : ''}`;
  } catch {
    return '';
  }
}
